import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { Shield, Eye, EyeOff, AlertCircle, CheckCircle2, ArrowLeft, KeyRound, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/errors";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { user, login, completeMfa, register, loading } = useAuth();
  // mode: login | register | forgot | reset | mfa
  const [mode, setMode] = useState("login");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ssoSubmitting, setSsoSubmitting] = useState(false);

  // Forgot/reset state
  const [resetToken, setResetToken] = useState("");
  const [issuedToken, setIssuedToken] = useState(""); // displayed once after forgot-password
  const [newPassword, setNewPassword] = useState("");

  // Deep link from the password-reset email: /login?reset_token=...
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const urlToken = searchParams.get("reset_token");
    if (urlToken) {
      setResetToken(urlToken);
      setMode("reset");
      // Strip the token from the URL so it doesn't linger in history
      setSearchParams({}, { replace: true });
    }
    const ssoError = searchParams.get("sso_error");
    if (ssoError) {
      setError(ssoError);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0e1a]">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }
  if (user) return <Navigate to="/app" replace />;

  const clearMessages = () => { setError(""); setInfo(""); };
  const switchMode = (next) => {
    clearMessages();
    setIssuedToken("");
    setMode(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setSubmitting(true);
    try {
      if (mode === "login") {
        const data = await login(email, password);
        if (data?.mfa_required) {
          setMfaToken(data.mfa_token);
          setMfaCode("");
          setUseRecovery(false);
          switchMode("mfa");
          setInfo("Enter the 6-digit code from your authenticator app.");
        }
      } else if (mode === "mfa") {
        await completeMfa(mfaToken, mfaCode.trim());
      } else if (mode === "register") {
        if (!name.trim()) {
          setError("Name is required");
          setSubmitting(false);
          return;
        }
        await register(email, password, name);
      } else if (mode === "forgot") {
        const { data } = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
        if (data.reset_token) {
          // Email delivery failed — surface the token so an admin can hand it
          // to the user via their own channel (Slack, helpdesk).
          setInfo(data.message || "A reset token has been generated.");
          setIssuedToken(data.reset_token);
        } else {
          setInfo(data.message || "If the email is registered, a password reset link has been sent to your inbox.");
        }
      } else if (mode === "reset") {
        await axios.post(`${API_URL}/api/auth/reset-password`, {
          token: resetToken.trim(),
          new_password: newPassword,
        });
        setInfo("Password reset successfully. You can sign in with your new password.");
        setResetToken("");
        setNewPassword("");
        // Auto-switch to login after a beat
        setTimeout(() => switchMode("login"), 1200);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(issuedToken);
      setInfo("Reset token copied to clipboard.");
    } catch {
      // ignore
    }
  };

  const handleSso = async () => {
    clearMessages();
    if (!email.trim()) {
      setError("Enter your work email first, then click Sign in with SSO.");
      return;
    }
    setSsoSubmitting(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/sso/check`, {
        params: { email: email.trim() },
      });
      if (data.sso) {
        window.location.assign(`${API_URL}${data.login_url}`);
      } else {
        setError("Single sign-on is not enabled for your organization. Sign in with your password instead.");
        setSsoSubmitting(false);
      }
    } catch (err) {
      setError(formatApiError(err));
      setSsoSubmitting(false);
    }
  };


  // === Render helpers ===
  const Title = () => {
    if (mode === "login") return "Sign in to your account";
    if (mode === "register") return "Create your account";
    if (mode === "forgot") return "Reset your password";
    if (mode === "mfa") return "Two-factor verification";
    return "Set a new password";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0e1a] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 25%, #3b82f6 1px, transparent 1px), radial-gradient(circle at 75% 75%, #3b82f6 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">
              FractionalRisk<span className="text-blue-400">.ai</span>
            </span>
          </div>
          <p className="text-slate-400 text-sm">Enterprise Risk Management Platform</p>
        </div>

        <div
          data-testid="auth-card"
          className="bg-[#111827]/80 backdrop-blur-xl border border-slate-700/50 rounded-xl p-8 shadow-2xl"
        >
          <h2 data-testid="auth-title" className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            {(mode === "forgot" || mode === "reset" || mode === "mfa") && (
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="text-slate-400 hover:text-white"
                data-testid="auth-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <Title />
          </h2>

          {error && (
            <div
              data-testid="auth-error"
              className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-lg mb-5"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="break-words">{error}</span>
            </div>
          )}

          {info && (
            <div
              data-testid="auth-info"
              className="flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm px-4 py-3 rounded-lg mb-5"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="break-words">{info}</span>
            </div>
          )}

          {issuedToken && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs px-3 py-3 rounded-lg mb-5 space-y-2" data-testid="issued-reset-token">
              <p className="font-semibold flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" />
                Reset token (single-use, expires in 60 min)
              </p>
              <p className="text-amber-200/80">
                Email delivery was unavailable — share this token with the user via your own channel.
              </p>
              <div className="flex gap-2 items-center">
                <code className="flex-1 bg-slate-900/60 rounded p-2 break-all font-mono text-[10px]">
                  {issuedToken}
                </code>
                <Button type="button" size="sm" variant="outline" onClick={copyToken} data-testid="copy-reset-token-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <button
                type="button"
                className="text-cyan-300 hover:text-cyan-200 underline text-[11px]"
                onClick={() => { setResetToken(issuedToken); switchMode("reset"); setResetToken(issuedToken); }}
                data-testid="use-token-btn"
              >
                Use this token now →
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-slate-300 text-sm">Full Name</Label>
                <Input
                  data-testid="register-name-input"
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  className="mt-1.5 bg-slate-800/50 border-slate-600/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                  required
                />
              </div>
            )}

            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <div>
                <Label htmlFor="email" className="text-slate-300 text-sm">Email</Label>
                <Input
                  data-testid="login-email-input"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-1.5 bg-slate-800/50 border-slate-600/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                  required
                />
              </div>
            )}

            {(mode === "login" || mode === "register") && (
              <div>
                <Label htmlFor="password" className="text-slate-300 text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    data-testid="login-password-input"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    className="bg-slate-800/50 border-slate-600/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 pr-10"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {mode === "login" && (
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => switchMode("forgot")}
                      className="text-xs text-slate-400 hover:text-blue-300 transition-colors"
                      data-testid="auth-forgot-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>
            )}

            {mode === "mfa" && (
              <div>
                <Label htmlFor="mfa-code" className="text-slate-300 text-sm">
                  {useRecovery ? "Recovery Code" : "Authenticator Code"}
                </Label>
                <Input
                  data-testid="mfa-code-input"
                  id="mfa-code"
                  type="text"
                  inputMode={useRecovery ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder={useRecovery ? "XXXX-XXXX" : "123456"}
                  className="mt-1.5 bg-slate-800/50 border-slate-600/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 font-mono tracking-widest text-center text-lg"
                  required
                  autoFocus
                  minLength={6}
                  maxLength={useRecovery ? 24 : 8}
                />
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => { setUseRecovery(!useRecovery); setMfaCode(""); }}
                    className="text-xs text-slate-400 hover:text-blue-300 transition-colors"
                    data-testid="mfa-toggle-recovery"
                  >
                    {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
                  </button>
                </div>
              </div>
            )}

            {mode === "reset" && (
              <>
                <div>
                  <Label htmlFor="reset-token" className="text-slate-300 text-sm">Reset Token</Label>
                  <Input
                    data-testid="reset-token-input"
                    id="reset-token"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Paste the token from your email/admin"
                    className="mt-1.5 bg-slate-800/50 border-slate-600/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 font-mono text-xs"
                    required
                    minLength={16}
                  />
                </div>
                <div>
                  <Label htmlFor="new-password" className="text-slate-300 text-sm">New Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      data-testid="reset-new-password-input"
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      className="bg-slate-800/50 border-slate-600/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 pr-10"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-11"
            >
              {submitting
                ? "Please wait..."
                : mode === "login"
                ? "Sign In"
                : mode === "register"
                ? "Create Account"
                : mode === "forgot"
                ? "Send reset link"
                : mode === "mfa"
                ? "Verify"
                : "Reset password"}
            </Button>
          </form>

          {mode === "login" && (
            <div className="mt-5">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-700/60" />
                <span className="text-[11px] uppercase tracking-wider text-slate-500">or</span>
                <div className="flex-1 h-px bg-slate-700/60" />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleSso}
                disabled={ssoSubmitting}
                data-testid="sso-login-button"
                className="w-full mt-4 border-slate-600/60 bg-slate-800/40 text-slate-200 hover:bg-slate-700/50 hover:text-white h-11"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {ssoSubmitting ? "Redirecting to your identity provider..." : "Sign in with SSO"}
              </Button>
            </div>
          )}

          {(mode === "login" || mode === "register") && (
            <div className="mt-6 text-center">
              <button
                data-testid="auth-toggle-mode"
                onClick={() => switchMode(mode === "login" ? "register" : "login")}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                {mode === "login"
                  ? "Don't have an account? Create one"
                  : "Already have an account? Sign in"}
              </button>
            </div>
          )}

          {mode === "forgot" && !issuedToken && (
            <div className="mt-6 text-center">
              <button
                onClick={() => switchMode("reset")}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                data-testid="have-token-link"
              >
                Already have a reset token? Enter it →
              </button>
            </div>
          )}
        </div>

        <p className="text-center mt-6 text-slate-500 text-xs">
          Fractional Risk, Inc. &mdash; AI-Powered Enterprise Risk Management
        </p>
      </div>
    </div>
  );
}
